import {
  Children,
  PointerEventHandler,
  cloneElement,
  isValidElement,
  useImperativeHandle,
  useRef,
} from "react";
import { Unselected, useAutoComplete } from "./common";
import { AutoCompleteListItemProps, AutoCompleteListProps } from "./props";
import { useSignalValue } from "@/utils/signal";
import { cn } from "@/utils/tailwind";

export function AutoCompleteList<T>(props: AutoCompleteListProps) {
  const { children: children_, className, ...listProps } = props;
  const { selected: selected_, list } = useAutoComplete();
  const selected = useSignalValue(selected_);

  const items: T[] = [];
  const children = Children.map(children_, (child, index) => {
    if (isValidElement<AutoCompleteListItemProps<T>>(child)) {
      const { value } = child.props;
      items.push(value);
      const onPointerOver: PointerEventHandler<HTMLLIElement> = (evt) => {
        child.props.onPointerOver?.(evt);
        if (!evt.defaultPrevented) {
          selected_.value = { index, value };
        }
      };
      return cloneElement(child, {
        "aria-selected": selected.index === index,
        onPointerOver,
      });
    }

    return child;
  });

  const items_ = useRef(items);
  items_.current = items;

  useImperativeHandle(
    list,
    () => ({
      selectBy(diff) {
        const current = selected_.value.index;
        const count = items_.current.length;
        const index =
          current !== undefined
            ? (((current + diff) % count) + count) % count
            : 0;
        const value = items_.current[index];
        selected_.value = { index, value };
      },

      reset(index) {
        if (
          index !== undefined &&
          index >= 0 &&
          index < items_.current.length
        ) {
          selected_.value = {
            index,
            value: items_.current[index],
          };
        } else {
          selected_.value = {
            index: undefined,
            value: Unselected,
          };
        }
      },
    }),
    [selected_, items_],
  );

  return (
    <ul className={cn("flex flex-col gap-1 p-1", className)} {...listProps}>
      {children}
    </ul>
  );
}

export function AutoCompleteListItem<T>(props: AutoCompleteListItemProps<T>) {
  const { value: _value, className, ...itemProps } = props;
  return (
    <li
      className={cn(
        "flex-1 flex items-center text-sm rounded-sm aria-selected:bg-grey-100 hover:bg-grey-50 px-2 py-1",
        className,
      )}
      {...itemProps}
    />
  );
}
